import React, { useEffect, useState } from "react";

import { Formik, Form } from "formik";
import Yup from "yup";
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Grid,
} from "@material-ui/core";
import {
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Loader from "./Loader";
import Tick from "../images/TickWebsite.png";

import {
  alphaNumValidation,
  numberValidation,
  useStyleObject,
  inlineStyleObject,
} from "./util";
import ExpandContainer from "./ExpandContainer";

const useStyles = makeStyles(useStyleObject);
let BASEURL =
  process.env.REACT_APP_STAGE === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

const Register = () => {
  const classes = useStyles();
  const [showLoading, setShowLoading] = useState(false);
  const [isRegSuccess, setIsRegSuccess] = useState(false);
  const [showFailureMsg, setShowFailureMsg] = useState("");
  const [link, setLink] = useState("");
  const [type, setType] = useState("proTrail");
  const stripe = useStripe();
  const elements = useElements();

  const [isUpgrade, setIsUpgrade] = useState(false);
  const [initialValues, setInitialValue] = useState({});
  const [reregister, setReregister] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Enter a valid email").required(" "),
    company: Yup.string()
      .min(3, "Company name should be at least 3 characters")
      .required(" "),
    phone: Yup.number().min(8, "Phone should be at least 10 characters"),
    termsCheck: Yup.boolean()
      .oneOf([true], "Please confirm that you have read the T&C")
      .required("Please confirm that you have read the T&C"),
  });

  useEffect(() => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });
    let qvalue = params.tid;
    if (qvalue) {
      getTenantDetails(qvalue);
    } else {
      setInitialValue({
        email: "",
        company: "",
        phone: "",
        termsCheck: false,
      });
    }
  }, []);

  const getTenantDetails = async (teid) => {
    setShowLoading(true);
    setShowFailureMsg("");
    const apiURL =
      process.env.REACT_APP_STAGE === "production"
        ? BASEURL + "/prod/registration/getaccountdetail"
        : BASEURL + "/V1/registration/getaccountdetail";
    try {
      const res = await fetch(apiURL, {
        body: undefined,
        method: "GET",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
          tenantid: teid,
        },
      });
      const response = await res.json();
      if (response.messageStatus === "Success") {
        setShowLoading(false);
        setIsUpgrade(true);
        const { tenantEmail, tenantName, tenantPhone } = response.user;
        setInitialValue({
          email: tenantEmail,
          company: tenantName,
          phone: tenantPhone,
          termsCheck: true,
        });
      } else {
        setShowLoading(false);
        setShowFailureMsg(response.internalMessage);
      }
    } catch (error) {
      setShowLoading(false);
      setShowFailureMsg(
        "Getting tenant details unsuccessful. Please try again."
      );
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!stripe || !elements) {
      return;
    }
    const cardElementContainer = document.querySelector("#cardNumber");
    const expiryElementContainer = document.querySelector("#cardExpiry");
    const cvcElementContainer = document.querySelector("#cardCVC");
    let cardElementEmpty = isUpgrade
      ? cardElementContainer.classList.contains("StripeElement--empty")
      : [];
    let expiryElementEmpty = isUpgrade
      ? expiryElementContainer.classList.contains("StripeElement--empty")
      : [];
    let cvcElementEmpty = isUpgrade
      ? cvcElementContainer.classList.contains("StripeElement--empty")
      : [];
    //console.log("cardElementEmpty...", cardElementEmpty);
    //console.log("expiryElementEmpty...", expiryElementEmpty);
    //console.log("cvcElementEmpty...", cvcElementEmpty);

    if (cardElementEmpty && expiryElementEmpty && cvcElementEmpty) {
      regiterAPI(values);
    } else {
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
      });
      //console.log("[PaymentMethod]", result);
      if (result && result.paymentMethod && result.paymentMethod.id) {
        values.paymentId = result.paymentMethod.id;
        regiterAPI(values);
      }
    }
    setSubmitting(false);
  };

  const regiterAPI = async (details) => {
    setShowLoading(true);
    setShowFailureMsg("");
    setLink("");
    setReregister(false);
    const payload = {
      tenantName: details.company,
      tenantEmail: details.email,
      tenantPhone: details.phone,
      //tenantTier: details.type,
      tenantTier: type,
      paymentId: details.paymentId,
    };
    const apiUrl =
      process.env.REACT_APP_STAGE === "production"
        ? BASEURL + "/prod/registration"
        : BASEURL + "/V1/registration";
    try {
      const res = await fetch(apiUrl, {
        body: JSON.stringify(payload),
        method: "POST",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
      });
      const response = await res.json();
      if (response.messageStatus === "Success") {
        const tlink =
          response.productType === "Pro"
            ? "https://pro.gabilabs.com/"
            : "https://lite.gabilabs.com/";
        setLink(tlink);
        setShowLoading(false);
        setIsRegSuccess(true);
        setReregister(response.reregister);
      } else {
        setShowLoading(false);
        setShowFailureMsg(response.internalMessage);
      }
    } catch (error) {
      setShowLoading(false);
      setShowFailureMsg("Registration Unsuccessful. Please try again.");
    }
  };

  const termsCheckboxLabel = (
    <div style={inlineStyleObject.termsContainer}>
      I have read and agree to Gabi's{" "}
      <a href="/#" style={inlineStyleObject.termsColor}>
        Terms of Use
      </a>{" "}
      and{" "}
      <a
        href="https://www.gabilabs.com/index.php/privacy-policy/"
        target="_blank"
        style={inlineStyleObject.termsColor}
      >
        Privacy Policy
      </a>
      .
    </div>
  );

  const regTitle = isUpgrade ? "Update payment details" : "Create an account";

  return (
    <>
      {showLoading && <Loader />}
      <div className={classes.container}>
        <h1 style={inlineStyleObject.title}>Welcome to the Gabi Experience</h1>
        {!isRegSuccess ? (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              isSubmitting,
            }) => (
              <Form>
                <Card className={classes.card}>
                  <CardContent className={classes.cardcontent}>
                    <Grid container>
                      <Grid item xs={6} style={inlineStyleObject.bg}>
                        <p className={classes.title}>Choose your product</p>
                        <RadioGroup
                          onChange={(e) => {
                            setType(e.target.value);
                          }}
                          name="type"
                          id="type"
                          value={type}
                          className={classes.formControlLabelRadio}
                        >
                          <Grid item xs={6}>
                            <FormControlLabel
                              disabled={true}
                              classes={{
                                root:
                                  type === "liteTrail"
                                    ? classes.formControlLabelNewRoot
                                    : classes.formControlLabelRoot,
                                label: classes.formControlLabel,
                              }}
                              value="liteTrail"
                              control={
                                <Radio
                                  color="primary"
                                  classes={{
                                    root: classes.radio,
                                    checked: classes.checked,
                                  }}
                                />
                              }
                              label={
                                <img
                                  src="https://gabi-prod.s3.us-east-2.amazonaws.com/image/Gabi-Lite.png"
                                  className={classes.liteImage}
                                  alt="lite"
                                />
                              }
                              labelPlacement="bottom"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              classes={{
                                root:
                                  type === "proTrail"
                                    ? classes.formControlLabelNewRoot
                                    : classes.formControlLabelRoot,
                                label: classes.formControlLabel,
                              }}
                              value="proTrail"
                              control={
                                <Radio
                                  color="primary"
                                  classes={{
                                    root: classes.radio,
                                    checked: classes.checked,
                                  }}
                                />
                              }
                              label={
                                <img
                                  src="https://gabi-prod.s3.us-east-2.amazonaws.com/image/Gabi-Pro.png"
                                  className={classes.liteImage}
                                  alt="pro"
                                />
                              }
                              labelPlacement="bottom"
                            />
                          </Grid>
                        </RadioGroup>
                      </Grid>
                      <Grid item xs={6} style={{ padding: "0 16px 16px 16px" }}>
                        <p className={classes.title}>{regTitle}</p>
                        {showFailureMsg && (
                          <p style={inlineStyleObject.regFailureMsg}>
                            {showFailureMsg}
                          </p>
                        )}
                        <TextField
                          id="company"
                          label="Full Name / Company"
                          value={values.company}
                          disabled={isUpgrade}
                          onChange={(e) => {
                            e.preventDefault();
                            if (alphaNumValidation(e.target.value))
                              handleChange(e);
                          }}
                          onBlur={handleBlur}
                          helperText={touched.company ? errors.company : ""}
                          error={touched.company && Boolean(errors.company)}
                          margin="dense"
                          variant="outlined"
                          InputLabelProps={isUpgrade ? { shrink: true } : {}}
                          fullWidth
                        />
                        <TextField
                          id="email"
                          label="Email"
                          value={values.email}
                          disabled={isUpgrade}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onKeyPress={(e) => {
                            if (e.which === 32 && !e.target.value) {
                              e.preventDefault();
                            }
                          }}
                          helperText={touched.email ? errors.email : ""}
                          error={touched.email && Boolean(errors.email)}
                          margin="dense"
                          variant="outlined"
                          InputLabelProps={isUpgrade ? { shrink: true } : {}}
                          fullWidth
                        />
                        <TextField
                          id="phone"
                          label="Phone"
                          value={values.phone}
                          disabled={isUpgrade}
                          onChange={(e) => {
                            e.preventDefault();
                            if (numberValidation(e.target.value))
                              handleChange(e);
                          }}
                          onBlur={handleBlur}
                          helperText={touched.phone ? errors.phone : ""}
                          error={touched.phone && Boolean(errors.phone)}
                          margin="dense"
                          variant="outlined"
                          maxlength="10"
                          InputLabelProps={isUpgrade ? { shrink: true } : {}}
                          fullWidth
                        />

                        {isUpgrade && <ExpandContainer upgrade={isUpgrade} />}

                        <FormControlLabel
                          control={
                            <Checkbox
                              id="termsCheck"
                              name="termsCheck"
                              color="primary"
                            />
                          }
                          id="termsCheck"
                          name="termsCheck"
                          label={termsCheckboxLabel}
                          checked={values.termsCheck ? true : false}
                          onChange={handleChange}
                          className={classes.formControlLabelRoot}
                        />
                        {touched.termsCheck &&
                        Boolean(errors.termsCheck) &&
                        errors.termsCheck ? (
                          <FormHelperText
                            error
                            className={classes.formHelperText}
                          >
                            {errors.termsCheck}
                          </FormHelperText>
                        ) : (
                          " "
                        )}
                        <CardActions className={classes.actions}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || !stripe}
                            style={inlineStyleObject.regButton}
                          >
                            {isUpgrade ? "Upgrade" : "Register"}
                          </Button>
                        </CardActions>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Form>
            )}
          </Formik>
        ) : null}
        {isRegSuccess && (
          <Card className={classes.card}>
            <CardContent>
              <div className={`${classes.logo} ${classes.mBottom}`}>
                <img src={Tick} className={classes.accImage} alt="logo" />
              </div>
              <p className={classes.title}>
                Account {!reregister ? "created" : "upgraded"} successfully!
              </p>
              {!reregister && (
                <p className={classes.subText}>
                  An email has been sent to your registered email address with
                  instructions and details to activate your account.
                </p>
              )}
              <p className={classes.subText}>
                {!reregister
                  ? `Please follow the link in the email to activate your account
                    or${" "}`
                  : ` Your account is upgraded successfully. You can now continue
                    to use your Gabi application${" "}`}
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={inlineStyleObject.clickHereBtn}
                >
                  Click Here
                </a>{" "}
                to go to the login page.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default Register;
